import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ByKey, NormalizedSingle } from '@industi/ngx-common';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { getInstanceLocale } from '../../instance/instance.reducer';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ArticleVideoContentEditAction,
  ArticleVideoContentEditActionTypes,
  ArticleVideoContentEditFailAction,
  ArticleVideoContentEditSuccessAction
} from './actions/edit.actions';
import {
  ArticleVideoContentLoadDetailsAction,
  ArticleVideoContentLoadDetailsActionTypes,
  ArticleVideoContentLoadDetailsFailAction,
  ArticleVideoContentLoadDetailsSuccessAction
} from './actions/details.actions';
import {
  ArticleVideoContentAddAction,
  ArticleVideoContentAddActionTypes,
  ArticleVideoContentAddFailAction,
  ArticleVideoContentAddSuccessAction
} from './actions/add.actions';
import { LibSnackbarComponent } from '@industi/ngx-modules';
import { ResponseStatus } from '../../../shared/enums/response-status.enum';
import { AlertUtil } from '../../../shared/utils/alert.util';
import { AppGlobal } from '../../../shared/app-global';
import { ArticleVideoContentService } from './video-content.service';

@Injectable()
export class ArticleVideoContentEffects {

  @Effect()
  add$ = this.actions$.pipe(
    ofType(ArticleVideoContentAddActionTypes.request),
    map((action: ArticleVideoContentAddAction) => action.payload),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([{ id, data }, locale]) => this.service.add(locale, data).pipe(
      map((res: NormalizedSingle) => new ArticleVideoContentAddSuccessAction({ res, id })),
      catchError((errors: HttpErrorResponse) => of(new ArticleVideoContentAddFailAction({ errors, id })))
    ))
  );

  @Effect()
  edit$ = this.actions$.pipe(
    ofType(ArticleVideoContentEditActionTypes.request),
    map((action: ArticleVideoContentEditAction) => action.payload),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([{ id, data }, locale]) => this.service.edit(locale, data, id).pipe(
      map((res: NormalizedSingle) => new ArticleVideoContentEditSuccessAction({ res, id })),
      catchError((errors: HttpErrorResponse) => of(new ArticleVideoContentEditFailAction({ errors, id })))
    ))
  );

  @Effect()
  loadDetails$ = this.actions$.pipe(
    ofType(ArticleVideoContentLoadDetailsActionTypes.request),
    map((action: ArticleVideoContentLoadDetailsAction) => action.payload.id),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([id, locale]) => this.service.details(locale, id).pipe(
      map((res: NormalizedSingle) => new ArticleVideoContentLoadDetailsSuccessAction({ id, res })),
      catchError((errors: HttpErrorResponse) => of(new ArticleVideoContentLoadDetailsFailAction({ id, errors })))
    ))
  );

  @Effect({ dispatch: false })
  crudFail$ = this.actions$.pipe(
    ofType(
      ArticleVideoContentAddActionTypes.requestFail,
      ArticleVideoContentEditActionTypes.requestFail
    ),
    map((action: ArticleVideoContentAddFailAction | ArticleVideoContentEditFailAction) => action.payload.errors),
    tap((data: HttpErrorResponse) => {
      if (data?.status === ResponseStatus.BAD_REQUEST || data?.status === ResponseStatus.UNPROCESSABLE_ENTITY) {
        this.snackBar.openFromComponent(LibSnackbarComponent, {
          data: {
            message: AlertUtil.getControlTranslation(data?.error?.detail),
            type: 'error'
          },
          duration: AppGlobal.defaultSnackBarDuration
        });
      }
    })
  );

  constructor(
    private actions$: Actions,
    private store: Store<ByKey>,
    private service: ArticleVideoContentService,
    private snackBar: MatSnackBar
  ) {
  }
}
